import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  adminApi,
  type AdminLetterOut,
  type LetterAutoReviewBatchResult,
  type PaginatedResponse,
} from '../../lib/api';

export function OutboundMailPage() {
  const [letters, setLetters] = useState<PaginatedResponse<AdminLetterOut> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchForce, setBatchForce] = useState(false);
  const [batchResult, setBatchResult] = useState<LetterAutoReviewBatchResult | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const selectAllRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const fetchLetters = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await adminApi.getLettersOut({
          page,
          status: statusFilter || undefined,
          type: typeFilter || undefined,
        });
        setLetters(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load letters');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLetters();
  }, [page, statusFilter, typeFilter]);

  useEffect(() => {
    setSelectedIds(new Set());
    setBatchResult(null);
    setBatchError(null);
  }, [page, statusFilter, typeFilter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-success/20 text-success';
      case 'failed':
        return 'bg-ember/20 text-ember';
      case 'queued':
        return 'bg-amber-500/20 text-amber-400';
      case 'pending':
        return 'bg-stone/30 text-mist';
      default:
        return 'bg-stone/30 text-mist';
    }
  };

  const getReviewBadge = (rating: number | null) => {
    if (rating === null) return 'bg-stone/30 text-mist';
    if (rating >= 8) return 'bg-success/20 text-success';
    if (rating >= 6) return 'bg-amber-500/20 text-amber-400';
    return 'bg-ember/20 text-ember';
  };

  const currentIds = letters?.data.map((letter) => letter.id) ?? [];
  const selectedCount = selectedIds.size;
  const isAllSelected = currentIds.length > 0 && currentIds.every((id) => selectedIds.has(id));
  const isSomeSelected = currentIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = isSomeSelected && !isAllSelected;
  }, [isSomeSelected, isAllSelected]);

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllSelected) {
        currentIds.forEach((id) => next.delete(id));
      } else {
        currentIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleBatchReview = async () => {
    if (selectedIds.size === 0 || batchLoading) return;
    setBatchLoading(true);
    setBatchError(null);
    setBatchResult(null);
    try {
      const result = await adminApi.requestLetterAutoReviewBatch({
        letterOutIds: Array.from(selectedIds),
        force: batchForce,
      });
      setBatchResult(result);
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : 'Failed to queue AI reviews');
    } finally {
      setBatchLoading(false);
    }
  };

  const skippedSummary = batchResult?.skipped?.length
    ? Object.entries(
        batchResult.skipped.reduce(
          (acc, item) => {
            acc[item.reason] = (acc[item.reason] ?? 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        ),
      )
        .map(([reason, count]) => `${reason.replace(/_/g, ' ')}: ${count}`)
        .join(', ')
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl text-parchment">Outbound Mail</h1>

        {/* Filters */}
        <div className="flex gap-2">
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
          >
            <option value="">All Types</option>
            <option value="confirmation">Confirmation</option>
            <option value="daily">Daily Letter</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="queued">Queued</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-ember/10 border border-ember/30 rounded-lg text-ember">{error}</div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="text-mist animate-pulse">Loading outbound mail...</div>
        </div>
      )}

      {/* Letters Table */}
      {!isLoading && letters && (
        <>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-stone/30 bg-stone/10 px-4 py-3">
              <div className="flex flex-wrap items-center gap-3 text-sm text-mist">
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-xs text-rune hover:underline"
                  disabled={currentIds.length === 0}
                >
                  {isAllSelected ? 'Deselect page' : 'Select page'}
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-xs text-mist hover:text-parchment"
                  disabled={selectedCount === 0}
                >
                  Clear selection
                </button>
                <span>{selectedCount} selected</span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-mist">
                  <input
                    type="checkbox"
                    className="h-3 w-3 rounded border-stone/40 bg-void"
                    checked={batchForce}
                    onChange={(e) => setBatchForce(e.target.checked)}
                  />
                  Force re-run
                </label>
                <button
                  type="button"
                  onClick={handleBatchReview}
                  disabled={selectedCount === 0 || batchLoading}
                  className="px-3 py-1.5 text-xs bg-rune text-void rounded-lg hover:bg-rune/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {batchLoading ? 'Queueing...' : 'Run AI review'}
                </button>
              </div>
            </div>

            {batchError && (
              <div className="rounded-lg border border-ember/30 bg-ember/10 px-4 py-2 text-xs text-ember">
                {batchError}
              </div>
            )}
            {batchResult && (
              <div className="rounded-lg border border-stone/30 bg-stone/10 px-4 py-2 text-xs text-mist">
                Queued {batchResult.queued.length} review
                {batchResult.queued.length === 1 ? '' : 's'}
                {batchResult.skipped.length > 0 && (
                  <> · Skipped {batchResult.skipped.length} ({skippedSummary})</>
                )}
                {batchResult.missing.length > 0 && (
                  <> · Missing {batchResult.missing.length}</>
                )}
              </div>
            )}
          </div>

          <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-mist text-left border-b border-stone/30 bg-stone/20">
                  <th className="px-4 py-3 font-medium w-10">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      className="h-3 w-3 rounded border-stone/40 bg-void"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all outbound letters on this page"
                    />
                  </th>
                  <th className="px-4 py-3 font-medium">Scheduled</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Subject</th>
                  <th className="px-4 py-3 font-medium">World</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">AI Review</th>
                  <th className="px-4 py-3 font-medium">Sent</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {letters.data.map((letter) => (
                  <tr
                    key={letter.id}
                    className="border-b border-stone/20 last:border-0 hover:bg-stone/10"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="h-3 w-3 rounded border-stone/40 bg-void"
                        checked={selectedIds.has(letter.id)}
                        onChange={() => toggleSelectOne(letter.id)}
                        aria-label={`Select outbound letter ${letter.subject}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-mist text-xs whitespace-nowrap">
                      {formatDate(letter.scheduledFor)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-stone/30 text-mist rounded text-xs">
                        {letter.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-parchment truncate max-w-xs">{letter.subject}</td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/worlds/${letter.worldId}`}
                        className="text-rune hover:underline text-xs font-mono"
                      >
                        {letter.worldAddress.split('@')[0]}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${getStatusColor(letter.status)}`}
                      >
                        {letter.status}
                      </span>
                      {letter.retryCount > 0 && (
                        <span className="ml-1 text-xs text-mist/50">
                          ({letter.retryCount} retries)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-mist">
                      {letter.autoReview ? (
                        <Link
                          to={`/admin/letters-out/${letter.id}#ai-review`}
                          className={`inline-flex px-2 py-0.5 rounded text-xs ${getReviewBadge(
                            letter.autoReview.rating,
                          )}`}
                          title="View AI review"
                        >
                          {letter.autoReview.rating}/10
                        </Link>
                      ) : (
                        <span className="text-mist/60">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-mist text-xs">
                      {letter.sentAt ? formatDate(letter.sentAt) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/letters-out/${letter.id}`}
                        className="text-rune hover:underline text-xs"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
                {letters.data.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-mist">
                      No outbound letters found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {letters.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-mist">{letters.pagination.total} total letters</div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-mist">
                  Page {page} of {letters.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === letters.pagination.totalPages}
                  className="px-3 py-1.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}
